import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import io from 'socket.io-client';
import { appURL, auth0Audience } from '../config';

const SocketContext = createContext();

const RECONNECT_DELAY_MS = 5000;

export const useSocket = () => {
    return useContext(SocketContext);
};

export const SocketProvider = ({ children }) => {
    const { isAuthenticated, getAccessTokenSilently } = useAuth0();

    const socket = useMemo(
        () =>
            io(appURL, {
                autoConnect: false,
                // The server verifies this token in the handshake. The function form runs on every
                // (re)connect, so an expired token is replaced before the next attempt.
                auth: (cb) =>
                    getAccessTokenSilently({ authorizationParams: { audience: auth0Audience } })
                        .then((token) => cb({ token }))
                        .catch(() => cb({})),
            }),
        [getAccessTokenSilently]
    );

    useEffect(() => {
        if (!isAuthenticated) {
            return undefined;
        }
        // A handshake the server rejects (expired token, blocked silent refresh) is terminal for
        // socket.io-client: it stops reconnecting (`socket.active === false`). Retry ourselves, or
        // live updates stay off for the rest of the session.
        let retryTimer = null;
        const onConnectError = () => {
            if (!socket.active && retryTimer === null) {
                retryTimer = setTimeout(() => {
                    retryTimer = null;
                    socket.connect();
                }, RECONNECT_DELAY_MS);
            }
        };
        socket.on('connect_error', onConnectError);
        socket.connect();

        return () => {
            socket.off('connect_error', onConnectError);
            clearTimeout(retryTimer);
            socket.disconnect();
        };
    }, [socket, isAuthenticated]);

    return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
};
